import Vue from 'vue';

// third party
import VueDragDrop from 'vue-drag-drop';
import VueGeolocation from 'vue-browser-geolocation';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fal } from '@fortawesome/pro-light-svg-icons';

// Font Awesome icon imports
// when importing the same icon from different sets, give each import an alias that includes the name of the set
// e.g. faSmile as faSmileRegular

import {
    faArrowsAlt,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faCircle as faCircleRegular,
    faClock,
    faCopy as faCopyRegular,
    faEye,
    faEyeSlash as faEyeSlashRegular,
    faFont,
    faMinus,
    faPencilAlt,
    faPlus,
    faSave,
    faSync,
    faTrashAlt,
    faSearch as faSearchRegular,
} from '@fortawesome/pro-regular-svg-icons';

import {
    faArrowAltUp,
    faArrowAltDown,
    faArrowAltLeft,
    faArrowAltRight,
    faArrowCircleLeft,
    faArrowCircleUp,
    faArrows,
    faBackward,
    faBars,
    faBinoculars,
    faBold,
    faBook,
    faCaretDown,
    faCaretLeft,
    faCaretRight,
    faCaretSquareDown,
    faCaretUp,
    faCircle as faCircleSolid,
    faCog,
    faCopy as faCopySolid,
    faCut,
    faDatabase,
    faEdit,
    faEnvelopeOpenText,
    faEyeSlash as faEyeSlashSolid,
    faFile,
    faFire,
    faFolderPlus,
    faHeart,
    faMinusCircle,
    faMouse,
    faMousePointer as faMousePointerSolid,
    faObjectGroup,
    faPaste,
    faPause,
    faPencil,
    faPlay,
    faPlusCircle,
    faQuestionCircle,
    faRedo,
    faSearch as faSearchSolid,
    faSignOutAlt,
    faSpinner,
    faSquare,
    faStar,
    faTablet,
    faTrash,
    faTh,
    faUndo,
} from '@fortawesome/pro-solid-svg-icons';

import {
    faMousePointer as faMousePointerDuotone,
    faSquareFull,
} from '@fortawesome/pro-duotone-svg-icons';

import {
    faAccessibleIcon,
    faFacebook,
    faFacebookSquare,
    faGoogle,
    faGooglePlus,
    faInstagram,
    faLinkedin,
    faStumbleuponCircle,
    faTwitter,
} from '@fortawesome/free-brands-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import VueScrollTo from 'vue-scrollto';
import VTooltip from 'v-tooltip';
import 'v-tooltip/dist/v-tooltip.css';

import {
    camelCase,
    paramCase,
    pascalCase,
    titleCase,
    lowerCase,
    upperCase,
    upperCaseFirst,
} from 'change-case';
import VCalendar from 'v-calendar';
import VueLazyload from 'vue-lazyload';

// app
import App from '@/App';
import router from '@/router/router';
import store from '@/store/store';
import pageModule from '@/store/modules/page';
import userModule from '@/store/modules/user';
import { SET_COMPONENT_LIST } from '@/store/types/mutation-types';

// utilities
import ButtonGroup from '@/components/utilities/button-group';
import Collapse from '@/components/utilities/collapse';
import ColorPicker from '@/components/utilities/color-picker';
import ContentEditable from '@/components/utilities/content-editable';
import Dropdown from '@/components/utilities/dropdown';
import DropdownCustom from '@/components/utilities/dropdown-custom';
import Slider from '@/components/utilities/slider';
import SliderMulti from '@/components/utilities/slider-multi';
import Toggle from '@/components/utilities/toggle';
import Tooltip from '@/components/utilities/tooltip';

// directives
import Group from '@/directives/group';
import PlainText from '@/directives/plaintext';
import TextSelect from '@/directives/text-select';

// filters
import pxFilter from '@/filters/px';
import remFilter from '@/filters/rem';
import ellipsesFilter from '@/filters/ellipses';
import formatSize from '@/filters/format-size';

// components
import Droppable from '@/components/page-editor/droppable';
import FlexElement from '@/flex-element/flex-element-smart';
import Modal from '@/components/shared/modal/modal';
import PrototypeBrowser from '@/components/page-editor/item-browser/prototype-browser';
import Snackbar from '@/components/shared/modal/snackbar';
import TemplateBrowser from '@/components/page-editor/item-browser/template-browser';
import ToolbarOption from '@/components/shared/toolbars/toolbar-option';

// exposed Methods
import { seattleLocalTime } from './utils/date';

// plugins
import VueHotKey from '@/plugins/vue-hotkey';
import { createProvider } from './vue-apollo';

// styles
import '@/styles/styles.scss';

Vue.use(VueDragDrop);
Vue.use(VueScrollTo);
Vue.use(VTooltip);
Vue.use(VueHotKey);
Vue.use(VueGeolocation);

Vue.directive('group', Group);
Vue.directive('plaintext', PlainText);
Vue.directive('text-select', TextSelect);

Vue.component('button-group', ButtonGroup);
Vue.component('collapse', Collapse);
Vue.component('color-picker', ColorPicker);
Vue.component('content-editable', ContentEditable);
Vue.component('dropdown', Dropdown);
Vue.component('dropdown-custom', DropdownCustom);
Vue.component('flex-element', FlexElement);
Vue.component('droppable', Droppable);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('modal', Modal);
Vue.component('prototype-browser', PrototypeBrowser);
Vue.component('slider', Slider);
Vue.component('slider-multi', SliderMulti);
Vue.component('snackbar', Snackbar);
Vue.component('template-browser', TemplateBrowser);
Vue.component('toggle', Toggle);
Vue.component('toolbar-option', ToolbarOption);
Vue.component('tooltip', Tooltip);

// Casing filters
Vue.filter('camelCase', camelCase);
Vue.filter('paramCase', paramCase);
Vue.filter('pascalCase', pascalCase);
Vue.filter('titleCase', titleCase);
Vue.filter('lowerCase', lowerCase);
Vue.filter('upperCase', upperCase);
Vue.filter('upperCaseFirst', upperCaseFirst);

// Custom filters
Vue.filter('px', pxFilter);
Vue.filter('rem', remFilter);
Vue.filter('ellipses', ellipsesFilter);
Vue.filter('formatSize', formatSize);

Vue.use(VCalendar, { });
Vue.use(VueLazyload, { observer: true });

function getComponentName(fileName) {
    return upperCaseFirst(
        camelCase(
            fileName
                .split('/')
                .pop()
                .replace(/\.\w+$/, ''),
        ),
    );
}

// Dynamic component imports
// note that we need to pass as much of our file path as possible, as a string literal
// ... as Webpack will automatically fetch all modules inside the literal portion of the path

const components = [];

// globally register all flex elements, excluding the dumb atoms
const userComponents = require.context('./components-user', true, /^((?!\/dumb|minimal\/).)+$/);
userComponents.keys().filter(f => f.endsWith('.vue')).forEach((fileName) => {
    const componentName = getComponentName(fileName);

    Vue.component(componentName, () => import(`./components-user/${ fileName.slice(2) }`));
    components.push(fileName.slice(fileName.lastIndexOf('/') + 1, -4));
});

// globally register all client flex elements
const clientComponents = require.context('./client/components-user', true, /^((?!\/dumb|minimal\/).)+$/);
clientComponents.keys().filter(f => f.endsWith('.vue')).forEach((fileName) => {
    const componentName = getComponentName(fileName);

    Vue.component(componentName, () => import(`./client/components-user/${ fileName.slice(2) }`));
    components.push(fileName.slice(fileName.lastIndexOf('/') + 1, -4));
});

// globally register all standard options
const userOptions = require.context('./options/components', true);
userOptions.keys().forEach((fileName) => {
    const componentName = getComponentName(fileName);

    Vue.component(componentName, () => import(`./options/components/${ fileName.slice(2) }`));
});


// globally register all minimal flex elements, excluding the dumb and smart atoms
const userMinimalComponents = require.context('./components-user', true, /^((?!\/dumb|smart\/).)+$/);
userMinimalComponents.keys().filter(f => f.endsWith('.vue')).forEach((fileName) => {
    const componentName = `${ getComponentName(fileName) }`;

    Vue.component(componentName, () => import(`./components-user/${ fileName.slice(2) }`));
    components.push(`${ fileName.slice(fileName.lastIndexOf('/') + 1, -4) }`);
});
//

// globally register all client options
const clientOptions = require.context('./client/options/components', true);
clientOptions.keys().forEach((fileName) => {
    const componentName = getComponentName(fileName);

    Vue.component(componentName, () => import(`./client/options/components/${ fileName.slice(2) }`));
});

store.registerModule('page', pageModule);
store.registerModule('user', userModule);
store.commit(SET_COMPONENT_LIST, components);

// register Font Awesome icons
library.add(
    // libraries
    fal,

    // individual icons
    faAccessibleIcon,
    faArrows,
    faArrowsAlt,
    faArrowAltUp,
    faArrowAltDown,
    faArrowAltLeft,
    faArrowAltRight,
    faArrowCircleLeft,
    faArrowCircleUp,
    faBackward,
    faBars,
    faBinoculars,
    faBold,
    faBook,
    faCaretDown,
    faCaretLeft,
    faCaretRight,
    faCaretSquareDown,
    faCaretUp,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faCircleRegular,
    faCircleSolid,
    faClock,
    faCog,
    faCopyRegular,
    faCopySolid,
    faCut,
    faDatabase,
    faEdit,
    faFile,
    faFire,
    faEnvelopeOpenText,
    faEye,
    faEyeSlashRegular,
    faEyeSlashSolid,
    faFacebook,
    faFacebookSquare,
    faFolderPlus,
    faFont,
    faGoogle,
    faGooglePlus,
    faHeart,
    faInstagram,
    faLinkedin,
    faMinus,
    faMinusCircle,
    faMouse,
    faMousePointerDuotone,
    faMousePointerSolid,
    faObjectGroup,
    faPaste,
    faPause,
    faPencil,
    faPencilAlt,
    faPlay,
    faPlus,
    faMinus,
    faPlusCircle,
    faQuestionCircle,
    faRedo,
    faSave,
    faSearchRegular,
    faSearchSolid,
    faSignOutAlt,
    faSpinner,
    faSquareFull,
    faSquare,
    faStar,
    faStumbleuponCircle,
    faSync,
    faTablet,
    faTh,
    faTrash,
    faTrashAlt,
    faUndo,
    faTwitter,
);

Vue.config.productionTip = false;

const hostName = window.location.hostname;
document.domain = hostName.substring(hostName.lastIndexOf('.', hostName.lastIndexOf('.') - 1) + 1);
window.seattleLocalTime = seattleLocalTime;

new Vue({
    router,
    store,
    apolloProvider: createProvider(),
    render: h => h(App),
}).$mount('#app');
